/**
 * Handterminal (Android, Lagergerät ohne Verkaufsrecht): Einrichtung und Anmeldung am
 * Gerät. Der Lagergeräte-Schlüssel liegt wie der Kassenschlüssel im localStorage
 * (Gerätezugang, im Backoffice widerrufbar); die Bediener-Anmeldung nur in der
 * Browsersitzung und mit Abmeldung nach 15 Minuten Ruhe.
 */
import type { Speicher } from './bediener';
import type { InventurStand } from './lager';
import type { GeraeteInventurlauf, GeraetSelbstauskunft } from './lagergeraet-api';

const SCHLUESSEL = 'bilanx.handterminal.schluessel';
const GERAET = 'bilanx.handterminal.geraet';
const MODUS = 'bilanx.handterminal.modus';
const ANMELDUNG = 'bilanx.handterminal.anmeldung';

export interface EntfernbarerSpeicher extends Speicher {
  removeItem(schluessel: string): void;
}

/** Format wie am Server (lagergeraet.ts): Präfix, nur base64url, kein Punkt. */
export function istGeraeteschluessel(text: string): boolean {
  return /^lagergeraet_[A-Za-z0-9_-]{20,}$/.test(text.trim());
}

/** Start der App als Handterminal: `?handterminal=1` oder einmal eingerichtet. */
export function istHandterminalModus(suche: string, speicher: Speicher): boolean {
  return new URLSearchParams(suche).get('handterminal') === '1' || speicher.getItem(MODUS) === '1';
}

export function ladeSchluessel(speicher: Speicher): string | null {
  const s = speicher.getItem(SCHLUESSEL);
  return s !== null && istGeraeteschluessel(s) ? s : null;
}

export function ladeGeraet(speicher: Speicher): GeraetSelbstauskunft | null {
  const roh = speicher.getItem(GERAET);
  if (roh === null || roh === '') {
    return null;
  }
  try {
    const g = JSON.parse(roh) as Partial<GeraetSelbstauskunft>;
    if (
      typeof g.geraet?.id === 'number' &&
      typeof g.geraet.unternehmerId === 'string' &&
      typeof g.geraet.name === 'string'
    ) {
      return {
        geraet: g.geraet,
        unternehmer: g.unternehmer ?? null,
        module: { lager: g.module?.lager !== false, bediener: g.module?.bediener === true },
      };
    }
  } catch {
    // kaputter Eintrag – neu einrichten
  }
  return null;
}

export function speichereEinrichtung(
  speicher: Speicher,
  schluessel: string,
  geraet: GeraetSelbstauskunft,
): void {
  speicher.setItem(SCHLUESSEL, schluessel.trim());
  speicher.setItem(GERAET, JSON.stringify(geraet));
  speicher.setItem(MODUS, '1');
}

export function aktualisiereGeraet(speicher: Speicher, geraet: GeraetSelbstauskunft): void {
  speicher.setItem(GERAET, JSON.stringify(geraet));
}

/** Gerät abmelden: Schlüssel weg; ausstehende Buchungen bleiben in IndexedDB. */
export function entferneEinrichtung(speicher: EntfernbarerSpeicher): void {
  speicher.removeItem(SCHLUESSEL);
  speicher.removeItem(GERAET);
  speicher.removeItem(MODUS);
}

/** Ablagebereich der Buchungen: je Unternehmer – ein neu eingerichtetes Gerät sendet sie weiter. */
export function lagerKontext(unternehmerId: string): string {
  return `unternehmer:${unternehmerId}`;
}

/** Schlüssel der lokalen Erfassungsliste (ladeErfassung/speichereErfassung). */
export function listenSchluessel(unternehmerId: string): string {
  return `handterminal:${unternehmerId}`;
}

export interface LagerAnmeldung {
  id: number;
  name: string;
  rechte: { lager: boolean; artikel: boolean };
  sitzung: string;
  unternehmerId: string;
}

const AKTIVITAET = 'bilanx.handterminal.aktivitaet';

/** Letzte Bedienung merken – die Abmeldung nach 15 Minuten überlebt so ein Neuladen. */
export function vermerkeAktivitaet(speicher: Speicher, jetztMs: number): void {
  speicher.setItem(AKTIVITAET, String(jetztMs));
}

export function letzteAktivitaet(speicher: Speicher): number | null {
  const roh = speicher.getItem(AKTIVITAET);
  const n = roh === null ? NaN : Number(roh);
  return Number.isFinite(n) ? n : null;
}

/**
 * Angemeldeten Bediener lesen: nur für denselben Betrieb und nur, wenn seit der letzten
 * Bedienung weniger als `limitMs` vergangen sind (sonst gilt er als abgemeldet).
 */
export function ladeLagerAnmeldung(
  speicher: Speicher,
  unternehmerId: string,
  jetztMs: number = Date.now(),
  limitMs = 15 * 60_000,
): LagerAnmeldung | null {
  const zuletzt = letzteAktivitaet(speicher);
  if (zuletzt === null || jetztMs - zuletzt >= limitMs) {
    return null;
  }
  const roh = speicher.getItem(ANMELDUNG);
  if (roh === null || roh === '') {
    return null;
  }
  try {
    const a = JSON.parse(roh) as Partial<LagerAnmeldung>;
    if (
      typeof a.id === 'number' &&
      typeof a.name === 'string' &&
      typeof a.sitzung === 'string' &&
      a.unternehmerId === unternehmerId
    ) {
      return {
        id: a.id,
        name: a.name,
        sitzung: a.sitzung,
        unternehmerId,
        rechte: { lager: a.rechte?.lager === true, artikel: a.rechte?.artikel === true },
      };
    }
  } catch {
    // unlesbar → abgemeldet
  }
  return null;
}

export function speichereLagerAnmeldung(
  speicher: Speicher,
  a: LagerAnmeldung | null,
  jetztMs: number = Date.now(),
): void {
  speicher.setItem(ANMELDUNG, a === null ? '' : JSON.stringify(a));
  if (a !== null) {
    vermerkeAktivitaet(speicher, jetztMs);
  }
}

/**
 * Was darf die Oberfläche anbieten? Ohne Bedienerverwaltung entscheidet allein der
 * Geräteschlüssel; mit Verwaltung die Rechte des angemeldeten Bedieners. Maßgeblich bleibt
 * die Prüfung am Server – das hier blendet nur aus.
 */
export function oberflaechenRechte(
  verwaltet: boolean,
  anmeldung: LagerAnmeldung | null,
): { buchen: boolean; artikel: boolean } {
  if (!verwaltet) {
    return { buchen: true, artikel: true };
  }
  return {
    buchen: anmeldung?.rechte.lager === true,
    artikel: anmeldung?.rechte.artikel === true,
  };
}

export function standAusLaeufen(laeufe: readonly GeraeteInventurlauf[]): InventurStand {
  const offen = laeufe.find((l) => l.status === 'offen');
  return {
    letzteLaufId: laeufe.reduce((m, l) => Math.max(m, l.id), 0),
    offen: offen === undefined ? null : { id: offen.id, bezeichnung: offen.bezeichnung },
  };
}
